import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Form } from 'react-bootstrap';
import AppContext from 'Utils/AppContext';
import PageHeader from 'Utils/PageHeader';
import { engineLabels, EngineIcon, getDisplayDate } from 'Utils';
import { BiSearchAlt } from 'react-icons/bi';
import { BRAND, FONT_MONO } from 'Utils/styleTokens';
import {
  FILE_SOURCE_TYPE,
  getDataSourceNumericId,
  filterActionableConnections,
} from 'DataSources/datasourcesApi';
import { startDiscoveryWithRule, listDiscoverJobsPage } from 'DataDiscovery/discoverApi';
import { listSamplingRuleTemplates } from 'DataDiscovery/samplingRulesApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import AdoptedFromBadge from 'DataSources/AdoptedFromBadge';
import { startProfileJob } from 'Metadata/profileApi';
import { listDataSourceInventories } from 'Secure/api';

// The picker adapts its copy and CTA to the operation being started — reached from
// every landing page's Actions ▾ → "New X" item (data-sources.html mockup).
const ACTIONS = {
  discover: {
    title: 'New Discovery',
    pill: 'Discover',
    verb: 'Discover',
    subtitle: 'Choose a data source to scan for sensitive data.',
    back: '/discover',
  },
  profile: {
    title: 'New Profile',
    pill: 'Profile',
    verb: 'Profile',
    subtitle: 'Choose a data source to profile its schema and metadata.',
    back: '/metadata',
  },
  mask: {
    title: 'New Masking Job',
    pill: 'Mask',
    verb: 'Mask',
    subtitle: 'Choose a discovered data source to mask.',
    back: '/secure',
  },
  encrypt: {
    title: 'New Encryption Job',
    pill: 'Encrypt',
    verb: 'Encrypt',
    subtitle: 'Choose a discovered data source to encrypt.',
    back: '/secure',
  },
  default: {
    title: 'Select a data source',
    pill: 'New',
    verb: 'Select',
    subtitle: 'Choose a data source to run against.',
    back: '/',
  },
};

const KIND_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'database', label: 'Databases' },
  { key: 'file', label: 'Files' },
];

const kindOf = (connection) => (connection.type === FILE_SOURCE_TYPE ? 'file' : 'database');

// Masking/encryption inventories are built from discovery results, so only sources with
// at least one discovery run or a saved inventory are valid targets. Returns the Set of
// numeric data-source ids that qualify: one paginated sweep of the discover-jobs list,
// then a per-source inventory check for the remainder only.
const JOBS_PAGE_CAP = 20;

async function fetchSecureEligibleIds(connections) {
  const ids = new Set();
  let page = 0;
  let totalPages = 1;
  while (page < totalPages && page < JOBS_PAGE_CAP) {
    // eslint-disable-next-line no-await-in-loop
    const { pageDto, discoverJobs } = await listDiscoverJobsPage(page, 200);
    totalPages = pageDto && pageDto.totalPages != null ? Number(pageDto.totalPages) : 1;
    discoverJobs.forEach((job) => {
      if (job && job.dataSourceId != null) ids.add(Number(job.dataSourceId));
    });
    page += 1;
  }
  const uncovered = connections.filter((c) => {
    const id = getDataSourceNumericId({ id: c.id });
    return id != null && !ids.has(id);
  });
  await Promise.all(
    uncovered.map(async (c) => {
      const id = getDataSourceNumericId({ id: c.id });
      const inventories = await listDataSourceInventories(id).catch(() => []);
      if (Array.isArray(inventories) && inventories.length > 0) ids.add(id);
    })
  );
  return ids;
}

const engineLabelOf = (connection) =>
  connection.type === FILE_SOURCE_TYPE
    ? `File (${connection.vfsScheme || 'local'})`
    : engineLabels[connection.type] || connection.type || '';

function DataSourcePicker() {
  const history = useHistory();
  const location = useLocation();
  const { connections, setNotification } = useContext(AppContext);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [sortBy, setSortBy] = useState({ field: 'name', dir: 'asc' });
  const [busyId, setBusyId] = useState(null);

  const actionKey = new URLSearchParams(location.search).get('action') || 'default';
  const action = ACTIONS[actionKey] || ACTIONS.default;
  const isSecureAction = actionKey === 'mask' || actionKey === 'encrypt';

  // null = still loading (mask/encrypt only); a fetch failure falls back to showing all
  // sources rather than blocking the flow on a transient error.
  const [secureEligibleIds, setSecureEligibleIds] = useState(null);
  const [secureEligibilityLoading, setSecureEligibilityLoading] = useState(false);

  useEffect(() => {
    if (!isSecureAction) return undefined;
    let cancelled = false;
    setSecureEligibilityLoading(true);
    fetchSecureEligibleIds(filterActionableConnections(connections || []))
      .then((ids) => {
        if (cancelled) return;
        setSecureEligibleIds(ids);
        setSecureEligibilityLoading(false);
      })
      .catch((e) => {
        console.log('Could not load discovery/inventory eligibility for the picker', e);
        if (cancelled) return;
        setSecureEligibleIds(null);
        setSecureEligibilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSecureAction, connections]);

  const rows = useMemo(() => {
    let eligible = filterActionableConnections(connections || []).filter(
      (c) => getDataSourceNumericId({ id: c.id, tableData: { id: c.id } }) != null
    );
    if (isSecureAction) {
      // mongodb is maskable (mongodb→mongodb) but encryption jobs still don't support it.
      if (actionKey === 'encrypt') {
        eligible = eligible.filter((c) => String(c.type).toLowerCase() !== 'mongodb');
      }
      // dynamodb supports neither masking (platform rejects with ENGINE_NOT_MASKABLE)
      // nor encryption — never offer it for any secure action.
      eligible = eligible.filter((c) => String(c.type).toLowerCase() !== 'dynamodb');
      // Encryption is strictly in-place, so read-only (source-of-record) rows are locked.
      // Masking can run cross-source INTO a writable target, so read-only sources stay
      // pickable — the inventory page requires a target for them (server enforces the same:
      // the read-only check applies to the mutation target, not the read side).
      if (actionKey === 'encrypt') eligible = eligible.filter((c) => !c.readOnly);
      // Inventories are built from discovery results — only sources with a discovery run
      // or a saved inventory can be masked/encrypted.
      if (secureEligibilityLoading) {
        eligible = [];
      } else if (secureEligibleIds) {
        eligible = eligible.filter((c) => secureEligibleIds.has(getDataSourceNumericId({ id: c.id })));
      }
    }
    if (kind !== 'all') eligible = eligible.filter((c) => kindOf(c) === kind);
    const term = search.trim().toLowerCase();
    if (term) {
      eligible = eligible.filter((c) =>
        [c.name, engineLabelOf(c), kindOf(c)].some((v) => String(v || '').toLowerCase().includes(term))
      );
    }
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    return eligible.slice().sort((a, b) => {
      const av = String(a[sortBy.field] || (sortBy.field === 'engine' ? engineLabelOf(a) : '')).toLowerCase();
      const bv = String(b[sortBy.field] || (sortBy.field === 'engine' ? engineLabelOf(b) : '')).toLowerCase();
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [connections, actionKey, isSecureAction, secureEligibleIds, secureEligibilityLoading, kind, search, sortBy]);

  const toggleSort = (field) =>
    setSortBy((prev) => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));

  // Sampling rules, for the discover action only. The Data Discovery page's New Discovery
  // started a run on the default rule with no way to choose one, while the source editor's
  // Run-Discovery caret let you pick — so the same operation offered the choice in one place
  // and not the other (C2D-1208). Loaded from the same endpoint the caret uses.
  const [rules, setRules] = useState([]);
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);

  useEffect(() => {
    if (actionKey !== 'discover') {
      setRulesLoaded(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await listSamplingRuleTemplates();
        if (cancelled) return;
        setRules(list);
        // Same rule the caret preselects: the server-seeded built-in, else the first row.
        const def = (Array.isArray(list) && (list.find((r) => r.builtIn === true) || list[0])) || null;
        if (def && def.id != null) setSelectedRuleId(def.id);
      } catch (e) {
        if (cancelled) return;
        // Non-fatal: startDiscoveryWithRule falls back to the default on a null rule, which
        // is exactly the old behaviour — the picker just cannot offer a choice.
        console.log('Failed to load sampling rules for the picker', e);
      } finally {
        if (!cancelled) setRulesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [actionKey]);

  const run = async (connection) => {
    const id = getDataSourceNumericId({ id: connection.id, tableData: { id: connection.id } });
    if (id == null) return;
    if (actionKey === 'mask') {
      history.push(`/secure/mask/inventory/${encodeURIComponent(id)}`);
      return;
    }
    if (actionKey === 'encrypt') {
      history.push(`/secure/encrypt/inventory/${encodeURIComponent(id)}`);
      return;
    }
    setBusyId(connection.id);
    try {
      if (actionKey === 'profile' || actionKey === 'discover') {
        // Gate the start on a working connection; on failure the helper routes the
        // user to the source editor (navigation unmounts us, so no busy reset).
        const gate = await ensureConnectionTested(id, { history, setNotification, name: connection.name });
        if (!gate.ok) return;
      }
      if (actionKey === 'profile') {
        await startProfileJob(id);
        setNotification({ variant: 'success', message: `Profile started for ${connection.name}.` });
        history.push('/metadata');
      } else if (actionKey === 'discover') {
        await startDiscoveryWithRule(id, selectedRuleId);
        const picked = rules.find((r) => r.id === selectedRuleId);
        setNotification({
          variant: 'success',
          message: picked
            ? `Discovery started for ${connection.name} using ${picked.name}.`
            : `Discovery started for ${connection.name}.`,
        });
        history.push('/discover');
      } else {
        history.push(`/sources/source-editor/${encodeURIComponent(connection.id)}`);
      }
    } catch (e) {
      console.log(`Picker ${actionKey} failed for ${connection.name}`, e);
      setNotification({
        variant: 'danger',
        message: `Could not start ${action.pill.toLowerCase()} for ${connection.name}: ${e.message || e}`,
      });
      // Success paths navigate away (component unmounts) — only reset here.
      setBusyId(null);
    }
  };

  const headerCell = (label, field) => (
    <th
      style={{ cursor: 'pointer', padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}
      onClick={() => toggleSort(field)}
    >
      {label}
      {sortBy.field === field ? (sortBy.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div>
      <PageHeader
        icon={BiSearchAlt}
        header={action.title}
        eyebrow={action.pill}
        tooltip={action.subtitle}
      />
      <p className='sg-small' style={{ color: 'var(--ink-500)' }}>{action.subtitle}</p>
      <Card>
        <Card.Body>
          <div className='d-flex align-items-center mb-3' style={{ gap: 10 }}>
            <Form.Control
              type='text'
              placeholder='Search by name, engine, or kind…'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 320 }}
              data-testid='picker-search'
            />
            <div className='d-flex' style={{ gap: 6 }}>
              {KIND_FILTERS.map((f) => (
                <Button
                  key={f.key}
                  size='sm'
                  variant={kind === f.key ? 'dark' : 'outline-secondary'}
                  onClick={() => setKind(f.key)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            {actionKey === 'discover' && rulesLoaded && rules.length > 0 && (
              <div className='d-flex align-items-center' style={{ gap: 6 }}>
                <Form.Label
                  htmlFor='picker-sampling-rule'
                  className='sg-small mb-0'
                  style={{ color: 'var(--ink-500)', whiteSpace: 'nowrap' }}
                >
                  Sampling rule
                </Form.Label>
                <Form.Control
                  as='select'
                  id='picker-sampling-rule'
                  data-testid='picker-sampling-rule'
                  value={selectedRuleId == null ? '' : String(selectedRuleId)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const match = rules.find((r) => String(r.id) === raw);
                    setSelectedRuleId(match ? match.id : null);
                  }}
                  style={{ maxWidth: 260 }}
                >
                  {rules.map((r) => (
                    <option key={r.id} value={String(r.id)}>{r.name}</option>
                  ))}
                </Form.Control>
              </div>
            )}
            <div className='ml-auto sg-small' style={{ fontFamily: FONT_MONO, color: 'var(--ink-500)' }}>
              {rows.length} of {(connections || []).length} sources
            </div>
          </div>
          <table className='w-100' style={{ fontSize: 13 }} data-testid='picker-table'>
            <thead>
              <tr className='sg-eyebrow' style={{ fontSize: 11, color: 'var(--ink-400)', textAlign: 'left' }}>
                {headerCell('Name', 'name')}
                {headerCell('Engine', 'engine')}
                {headerCell('Kind', 'type')}
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Last tested</th>
                <th style={{ padding: '8px 10px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((connection) => (
                <tr key={connection.id} style={{ borderTop: '1px solid var(--ink-100)' }}>
                  <td style={{ padding: '8px 10px', fontFamily: FONT_MONO }}>
                    {connection.name}
                    <AdoptedFromBadge
                      registeredServiceId={connection.registeredServiceId}
                      registeredServiceName={connection.registeredServiceName}
                    />
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <EngineIcon
                      engine={connection.type === FILE_SOURCE_TYPE ? connection.vfsScheme : connection.type}
                    />{' '}
                    {engineLabelOf(connection)}
                  </td>
                  <td style={{ padding: '8px 10px', textTransform: 'capitalize' }}>{kindOf(connection)}</td>
                  <td style={{ padding: '8px 10px' }}>
                    {connection.lastTested ? getDisplayDate(connection.lastTested) : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <Button
                      size='sm'
                      style={{ background: BRAND.blue, borderColor: BRAND.blue }}
                      disabled={busyId === connection.id}
                      onClick={() => run(connection)}
                      data-testid={`picker-run-${connection.id}`}
                    >
                      {busyId === connection.id ? 'Starting…' : `${action.verb} →`}
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className='text-center sg-small' style={{ padding: 24, color: 'var(--ink-400)' }}>
                    {isSecureAction && secureEligibilityLoading
                      ? 'Checking discovery runs and inventories…'
                      : isSecureAction && !search.trim()
                      ? 'No eligible data sources. Only sources with a discovery run or a saved inventory can be selected — run a discovery first.'
                      : 'No data sources match your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card.Body>
      </Card>
    </div>
  );
}

export default DataSourcePicker;
export { ACTIONS };
